import { AlertTriangle, CheckCircle2, Loader2, Lock, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchUamStatus, requestUamChallenge, submitUamVerify } from '@/api/uam';

/**
 * Under-Attack Mode (UAM) full-screen challenge gate.
 *
 * Responsibility:
 *   1. On mount, poll /api/uam/status to know whether the gate is engaged
 *      and whether the visitor already has a valid pass cookie.
 *   2. If gate is ON and visitor has NO valid cookie, render this fullscreen
 *      challenge UI. Otherwise, render ``children`` transparently.
 *   3. The challenge consists of:
 *        (a) a manual checkbox the visitor must tick, AND
 *        (b) a SHA-256 proof-of-work puzzle solved silently in JS.
 *   4. On both passing, POST the solution to /api/uam/verify. The backend
 *      will Set-Cookie the signed pass and we reload to enter the site.
 *
 * The PoW solver runs in chunks via setTimeout(0) so the main thread never
 * freezes for more than ~50 ms at a time — the UI progress bar updates fluidly.
 */
const CHUNK_ITERATIONS = 5000; // hashes per yield
const MAX_ITERATIONS = 50_000_000; // safety stop — ~30 s on slow phones

async function sha256Hex(text) {
  const buf = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const UnderAttackGate = ({ children }) => {
  const [status, setStatus] = useState({ checked: false, enabled: false, passed: false, difficulty: 5, message: null });
  const [phase, setPhase] = useState('idle'); // idle | computing | ready | submitting | error
  const [checkbox, setCheckbox] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [nonce, setNonce] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null); // { token, challenge, difficulty, expires_at }
  const [errorMsg, setErrorMsg] = useState(null);
  const [iterCount, setIterCount] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const abortRef = useRef(false);

  // ---------- Status check on mount ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchUamStatus();
        if (!alive) return;
        setStatus({ checked: true, ...data });
      } catch {
        // If status itself fails (Cloudflare blip or transient backend), let
        // the SPA render normally rather than hard-block users.
        if (alive) setStatus({ checked: true, enabled: false, passed: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ---------- PoW solver ----------
  const startPow = useCallback(async () => {
    setPhase('computing');
    setErrorMsg(null);
    setProgressPct(0);
    setNonce(null);
    setIterCount(0);
    setElapsedMs(0);
    abortRef.current = false;

    let info;
    try {
      info = await requestUamChallenge();
      setTokenInfo(info);
    } catch (_err) {
      setPhase('error');
      setErrorMsg('Could not fetch challenge. Please refresh and try again.');
      return;
    }

    const { challenge, difficulty } = info;
    const target = '0'.repeat(difficulty);
    const startTs = performance.now();
    let counter = 0;

    const runChunk = async () => {
      if (abortRef.current) return;
      for (let i = 0; i < CHUNK_ITERATIONS; i += 1) {
        const digest = await sha256Hex(`${challenge}:${counter}`);
        if (digest.startsWith(target)) {
          const elapsed = performance.now() - startTs;
          setNonce(String(counter));
          setProgressPct(100);
          setIterCount(counter);
          setElapsedMs(Math.round(elapsed));
          setPhase('ready');
          return;
        }
        counter += 1;
      }
      if (counter >= MAX_ITERATIONS) {
        setPhase('error');
        setErrorMsg('Browser was unable to solve the challenge. Please refresh and try again.');
        return;
      }
      setIterCount(counter);
      const elapsed = performance.now() - startTs;
      setElapsedMs(Math.round(elapsed));
      // The success probability per hash is 16^-difficulty, so the expected
      // number of hashes is 16^difficulty. We use that to render a smooth
      // progress bar that caps at 95 % until the solution is actually found.
      const expected = 16 ** difficulty;
      const ratio = Math.min(0.95, counter / expected);
      setProgressPct(Math.round(ratio * 100));
      // Yield so the UI can paint.
      setTimeout(runChunk, 0);
    };
    runChunk();
  }, []);

  // ---------- Kick off PoW once the gate appears ----------
  useEffect(() => {
    if (status.checked && status.enabled && !status.passed && phase === 'idle') {
      startPow();
    }
  }, [status, phase, startPow]);

  // ---------- Cancel solver on unmount ----------
  useEffect(
    () => () => {
      abortRef.current = true;
    },
    [],
  );

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!checkbox || !nonce || !tokenInfo?.token) return;
    setPhase('submitting');
    setErrorMsg(null);
    try {
      await submitUamVerify(tokenInfo.token, nonce, true);
      // Hard reload so the new cookie is applied across every fetch.
      window.location.reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Verification failed.';
      setErrorMsg(typeof detail === 'string' ? detail : 'Verification failed. Please retry.');
      setPhase('ready');
    }
  };

  // ---------- Render guard ----------
  if (!status.checked) {
    return (
      <div className="sc-uam-loader" data-testid="uam-loader">
        <Loader2 className="w-5 h-5 animate-spin text-[#FFB612]" />
      </div>
    );
  }
  if (!status.enabled || status.passed) {
    return children;
  }

  // ---------- Gate UI ----------
  const canContinue = phase === 'ready' && checkbox && !!nonce;
  return (
    <div className="sc-uam-canvas" data-testid="uam-gate">
      <div className="sc-uam-grain" aria-hidden="true" />
      <div className="sc-uam-card" data-testid="uam-card">
        <div className="sc-uam-card__crest">
          <ShieldAlert className="w-8 h-8" strokeWidth={2.4} />
        </div>
        <div className="sc-uam-card__eyebrow">
          <Lock className="w-3 h-3" />
          Heightened Security Mode
        </div>
        <h1 className="sc-uam-card__title">Verifying your browser before we let you in.</h1>
        <p className="sc-uam-card__subtitle">
          {status.message ||
            '1SimpleCycler 3.0 is currently filtering all incoming traffic to protect the platform. Complete the one-time check below to continue.'}
        </p>

        {/* Checkbox */}
        <label className="sc-uam-checkbox" data-testid="uam-checkbox-label">
          <input type="checkbox" checked={checkbox} onChange={(e) => setCheckbox(e.target.checked)} data-testid="uam-checkbox" />
          <span className="sc-uam-checkbox__indicator" aria-hidden="true">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
          <span className="sc-uam-checkbox__text">I confirm I am a legitimate human visitor.</span>
        </label>

        {/* PoW progress */}
        <div className="sc-uam-pow" data-testid="uam-pow">
          <div className="sc-uam-pow__head">
            <div className="sc-uam-pow__label">
              {phase === 'computing' && (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Verifying your browser…
                </>
              )}
              {phase === 'ready' && (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#7dd87a]" />
                  Browser verified.
                </>
              )}
              {phase === 'submitting' && (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Issuing pass…
                </>
              )}
              {phase === 'error' && (
                <>
                  <AlertTriangle className="w-3.5 h-3.5 text-[#fca5a5]" />
                  Verification error.
                </>
              )}
            </div>
            <div className="sc-uam-pow__pct sc-tabular">{Math.min(100, progressPct)}%</div>
          </div>
          <div className="sc-uam-pow__track">
            <div className={`sc-uam-pow__fill ${phase === 'ready' ? 'sc-uam-pow__fill--done' : ''}`} style={{ width: `${Math.min(100, progressPct)}%` }} />
          </div>
          <div className="sc-uam-pow__meta">
            Difficulty {status.difficulty} · {iterCount.toLocaleString()} hashes
            {elapsedMs > 0 && <> · {(elapsedMs / 1000).toFixed(1)}s elapsed</>}
          </div>
        </div>

        {/* Continue */}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canContinue || phase === 'submitting'}
          className="sc-btn-gold sc-uam-continue"
          data-testid="uam-continue"
        >
          {phase === 'submitting' ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Issuing pass
            </>
          ) : (
            <>Continue to 1SimpleCycler 3.0</>
          )}
        </button>

        {errorMsg && (
          <div className="sc-uam-error" data-testid="uam-error">
            <AlertTriangle className="w-3.5 h-3.5" /> {errorMsg}
          </div>
        )}

        <div className="sc-uam-footer">
          This is an automated browser check. It runs once per 30 minutes and requires no input beyond ticking the checkbox above. No personal data is collected
          during verification.
        </div>
      </div>
    </div>
  );
};

export default UnderAttackGate;
